import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Clock } from "lucide-react";
import { format } from "date-fns";
import Seo from "@/components/common/Seo";
import PageHeader from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import { api, resolveImageUrl } from "@/lib/api";

const PAGE_SIZE = 9;

export default function Insights() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: reset ? 0 : offset, destination: "insights" };
      if (q) params.q = q;
      const { data } = await api.get("/blogs", { params });
      setItems(reset ? data.items : [...items, ...data.items]);
      setTotal(data.total);
      if (reset) setOffset(PAGE_SIZE); else setOffset(offset + PAGE_SIZE);
    } finally { setLoading(false); }
  };

  useEffect(() => { setOffset(0); load(true); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); setOffset(0); load(true); };

  return (
    <>
      <Seo
        title="Executive Insights"
        description="Executive Insights on technology strategy, business transformation, AI, ERP, governance and leadership by Aftab Azad, Executive Technology Advisor and Fractional CIO."
        path="/insights"
        keywords={["Executive Insights", "Fractional CIO", "Technology Strategy", "AI Strategy", "Business Transformation", "Executive Technology Advisor"]}
      />
      <PageHeader
        overline="Executive Insights"
        titleLines={["Perspectives for", "the business", "leader."]}
        subtitle="Ideas, frameworks and lessons from more than two decades leading technology through real business consequence. Written for growing businesses across India, the UAE and the Gulf Region."
        testId="insights-header"
      />

      <section data-testid="insights-list" className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-end gap-4 border-b border-slate-200 pb-6">
            <form onSubmit={onSearch} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2">
              <Search className="h-4 w-4 text-slate-400" />
              <input data-testid="insights-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" className="w-48 bg-transparent font-body text-sm outline-none" />
              <button className="rounded-full bg-navy px-3 py-1 font-body text-xs font-semibold text-white">Go</button>
            </form>
          </div>

          {loading && items.length === 0 ? (
            <p className="mt-16 text-center font-body text-slate-500">Loading executive insights…</p>
          ) : items.length === 0 ? (
            <div className="mt-16 text-center">
              <p className="font-heading text-2xl font-semibold text-navy">No articles yet.</p>
              <p className="mt-2 font-body text-slate-500">New Executive Insights will appear here as they are published.</p>
            </div>
          ) : (
            <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {items.map((b, i) => (
                <Reveal key={b.id} delay={(i % 3) * 0.05}>
                  <Link to={`/insights/${b.slug}`} data-testid={`insights-card-${b.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white transition-colors hover:border-royal">
                    {b.hero_image ? (
                      <div className="h-56 overflow-hidden bg-slate-100">
                        <img src={resolveImageUrl(b.hero_image)} alt={b.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    ) : (
                      <div className="flex h-56 items-center justify-center bg-gradient-to-br from-navy via-navy to-royal font-heading text-2xl text-white/40">{b.category}</div>
                    )}
                    <div className="flex flex-1 flex-col p-6">
                      <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-royal">{b.category}</p>
                      <h3 className="mt-3 font-heading text-xl font-bold leading-snug tracking-tight text-navy group-hover:text-royal">{b.title}</h3>
                      {b.excerpt && <p className="mt-3 line-clamp-3 flex-1 font-body text-sm leading-relaxed text-slate-600">{b.excerpt}</p>}
                      <div className="mt-5 flex items-center justify-between font-body text-xs text-slate-500">
                        <span>{b.published_at ? format(new Date(b.published_at), "d MMM yyyy") : ""}</span>
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {b.reading_time} min</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}

          {items.length < total && (
            <div className="mt-12 text-center">
              <button onClick={() => load(false)} disabled={loading} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 font-body text-sm font-semibold text-navy hover:bg-slate-50 disabled:opacity-60">
                {loading ? "Loading…" : "Load more"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      <CTASection
        overline="Book a conversation"
        heading={["Turn insight into", "a business decision."]}
        body="Book an executive strategy session to discuss how these ideas translate to your business, your market and your next stage of growth."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Contact Aftab", to: "/contact" }}
      />
    </>
  );
}
